import { useEffect, useState } from 'react';
import { useThemePreference } from '../../theme/ThemePreferenceProvider';
import type { OnboardingLanguage } from '../constants';
import type { GameMode } from '../state';

interface QuickStartGeneratingScreenProps {
  language?: OnboardingLanguage;
  isSubmitting?: boolean;
  submitCompleted?: boolean;
  submitError?: string | null;
  gameMode?: string | GameMode | null;
  onOpenGuidedDemo: () => void;
}

export function QuickStartGeneratingScreen({
  language = 'es',
  isSubmitting = false,
  submitCompleted = false,
  submitError = null,
  gameMode = null,
  onOpenGuidedDemo,
}: QuickStartGeneratingScreenProps) {
  const { theme } = useThemePreference();
  const isLight = theme === 'light';

  const copy = language === 'en'
    ? {
        title: 'Configuring your Quick Start',
        subtitle: 'Applying your selection with the same calibration feel used in onboarding.',
        bridgeHint: "Next, you'll enter Innerbloom with your first tasks and onboarding banners ready.",
        done: '',
        cta: 'Enter Innerbloom',
        saving: 'Saving your Quick Start…',
        steps: [
          'Balancing Body, Mind and Soul',
          'Calibrating your task difficulty',
          'Preparing your initial plan',
          'Activating your Rhythm: {mode}',
          'Activating your plan – 2 months included',
        ],
      }
    : {
        title: 'Configurando tu Inicio rápido',
        subtitle: 'Estamos aplicando tu selección y reutilizando el flujo real del onboarding.',
        bridgeHint: 'Ahora vas a entrar a Innerbloom con tus primeras tareas y banners de onboarding listos.',
        done: '',
        cta: 'Entrar a Innerbloom',
        saving: 'Guardando tu Quick Start…',
        steps: [
          'Equilibrando Cuerpo, Mente y Alma',
          'Calibrando la dificultad de tus tareas',
          'Preparando tu plan inicial',
          'Activando tu ritmo: {mode}',
          'Activando tu plan – 2 meses incluidos',
        ],
      };

  const modeLabel = gameMode ? String(gameMode).toUpperCase() : language === 'en' ? 'Flow' : 'Flow';
  const steps = copy.steps.map((step) => step.replace('{mode}', modeLabel));
  const [setupProgress, setSetupProgress] = useState(1);

  useEffect(() => {
    setSetupProgress(1);
    const timer = window.setInterval(() => {
      setSetupProgress((prev) => {
        if (prev >= steps.length) {
          window.clearInterval(timer);
          return prev;
        }
        return prev + 1;
      });
    }, 950);
    return () => window.clearInterval(timer);
  }, [steps.length]);

  return (
    <div className="quickstart-premium-root onboarding-premium-root min-h-screen min-h-dvh overflow-y-auto px-4 py-10">
      <section className="quickstart-premium-card quickstart-setup-card onboarding-premium-card relative mx-auto mt-5 w-full max-w-3xl rounded-3xl p-5 sm:p-8">
        <div className="mb-5 flex items-center justify-center gap-2 text-center text-[0.68rem] font-semibold uppercase tracking-[0.36em] text-[color:var(--color-text-subtle)] sm:text-xs">
          <span>Innerbloom</span>
          <img src="/IB-COLOR-LOGO-v2.png" alt="Innerbloom logo" className="h-[1.8em] w-auto" />
        </div>
        <h1 className={`text-balance text-2xl font-semibold sm:text-3xl ${isLight ? 'text-slate-900' : 'text-[color:var(--color-text)]'}`}>{copy.title}</h1>
        <p className={`mt-2 text-sm ${isLight ? 'text-slate-700' : 'text-[color:var(--color-text-muted)]'}`}>{copy.subtitle}</p>
        <ul className="mt-6 space-y-3">
          {steps.slice(0, setupProgress).map((setupStep, index) => {
            const complete = setupProgress >= steps.length;
            const isPlanStep = index === steps.length - 1;
            return (
              <li key={setupStep} className={`flex items-center gap-3 text-sm transition-all duration-500 ${isLight ? 'text-slate-700' : 'text-[color:var(--color-text-muted)]'}`}>
                <span className={`quickstart-setup-dot h-2.5 w-2.5 rounded-full ${!complete && index === setupProgress - 1 ? 'animate-pulse' : ''}`} />
                <span>
                  {setupStep}
                  {isPlanStep ? <span className="quickstart-free-pill ml-2 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] align-middle">FREE</span> : null}
                </span>
              </li>
            );
          })}
        </ul>
        <div className="quickstart-progress-track mt-6 h-1.5 w-full overflow-hidden rounded-full">
          <div className={`quickstart-progress-fill h-full transition-all duration-700 ${setupProgress >= steps.length ? 'w-full quick-start-setup__progress-complete' : 'w-1/3 quick-start-setup__progress-animated'}`} />
        </div>
        <p className={`mt-6 text-sm ${isLight ? 'text-slate-700' : 'text-[color:var(--color-text-muted)]'}`}>{copy.bridgeHint}</p>
        <div className="mt-8 flex flex-col items-center gap-3">
          <button type="button" onClick={onOpenGuidedDemo} disabled={isSubmitting || !submitCompleted} className="quickstart-primary-cta inline-flex items-center justify-center rounded-full border px-7 py-3 text-sm font-semibold transition duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-55">{isSubmitting ? copy.saving : submitCompleted ? copy.cta : copy.saving}</button>
          {submitCompleted ? <p className="text-sm text-emerald-500">{copy.done}</p> : null}
          {submitError ? <p className="text-sm text-rose-500">{submitError}</p> : null}
        </div>
        <style>{`
          .quick-start-setup__progress-animated { animation: quick-start-progress 2.3s ease-in-out infinite; transform-origin: left; }
          @keyframes quick-start-progress { 0% { transform: translateX(-112%);} 100% { transform: translateX(315%);} }
        `}</style>
      </section>
    </div>
  );
}
